import {
    FormState,
    nextPage,
    previousPage,
    selectForm,
    selectPage,
    skipImage,
    updateFormState,
    toPage,
    prepareImage,
    reset,
    downloadSecond,
} from '../slices/formSlice'
import './InputForm.scss'
import { Field, Form, FormSpy } from 'react-final-form'
import quotes from './quotes.json'
import { useCallback } from 'react'
import { useAppDispatch, useAppSelector, useExport, usePost } from '../hooks'
import SelectFile from './SelectFile'
import Preview from '../app/Preview'
import { isMobile } from 'react-device-detect'
import { toggleBackgroundImage } from '../slices/mediaSlice'

const FormStateToRedux = ({ form }: { form: string }) => {
    const dispatch = useAppDispatch()

    const toggleBackground = () => {
        dispatch(toggleBackgroundImage())
    }

    return (
        <FormSpy<FormState>
            subscription={{ values: true }}
            onChange={(state) =>
                dispatch(
                    updateFormState({
                        ...state.values,
                        age: state.values.age ? parseInt(state.values.age as unknown as string) : null,
                        quote:
                            state.values.quote !== 'custom'
                                ? parseInt(state.values.quote as unknown as string)
                                : 'custom',
                    }),
                )
            }
        />
    )
}

const Error = ({ name }: { name: string }) => (
    <Field
        name={name}
        subscription={{ touched: true, error: true }}
        render={({ meta: { touched, error } }) =>
            touched && error ? <span className="error-message">{error}</span> : null
        }
    />
)

function InputForm() {
    const form = useAppSelector(selectForm)
    const { age, quote, downloadedSecond } = form
    const page = useAppSelector(selectPage)
    const dispatch = useAppDispatch()
    const toPreviousPage = useCallback(() => dispatch(previousPage()), [dispatch])
    const toNextPage = useCallback(() => dispatch(nextPage()), [dispatch])
    const exportFile = useExport()
    const postTile = usePost()

    const toggleBackground = () => {
        dispatch(toggleBackgroundImage())
    }

    return (
        <Form<FormState>
            onSubmit={async () => {
                toNextPage()
                if (page !== 3) return

                const svg = await exportFile.export()
                const filteredSvg = svg.filter((x) => x !== undefined)
                if (filteredSvg.length === svg.length) {
                    postTile(filteredSvg as string[])
                }
            }}
            initialValues={form}
        >
            {({ handleSubmit, submitting, invalid }) => (
                <form onSubmit={handleSubmit}>
                    <FormStateToRedux form="example" />

                    {page === 0 && (
                        <div>
                            {form.image && (
                                <>
                                    <p>
                                        <img src={form.image} alt="Dein hochgeladenes Bild" className="col-100" />
                                    </p>
                                    <SelectFile
                                        selectedFile={({ content }) => {
                                            dispatch(prepareImage(content))
                                        }}
                                        className=""
                                    >
                                        Anderes Bild wählen
                                    </SelectFile>
                                    <button type="button" onClick={() => dispatch(skipImage())}>
                                        Bild entfernen
                                    </button>
                                </>
                            )}
                            {!form.image && (
                                <>
                                    <SelectFile
                                        selectedFile={({ content }) => {
                                            dispatch(prepareImage(content))
                                        }}
                                    />
                                </>
                            )}
                        </div>
                    )}
                    {page === 1 && (
                        <div>
                            <div className="col-25">
                                <label>Was möchtest Du sagen?</label>
                            </div>
                            <div className="col-75">
                                {quotes[age && age >= 18 ? '>=18' : '<18'].map((quote, i) => (
                                    <div key={i} className="radio-button">
                                        <Field<number>
                                            name="quote"
                                            component="input"
                                            type="radio"
                                            value={i}
                                            id={`quote_${i}`}
                                        />
                                        <label htmlFor={`quote_${i}`}>{quote}</label>
                                    </div>
                                ))}
                                <div className="radio-button">
                                    <Field<FormState['quote']>
                                        name="quote"
                                        component="input"
                                        type="radio"
                                        value="custom"
                                        id="quote_custom"
                                    />{' '}
                                    <label htmlFor="quote_custom">
                                        <span>
                                            {age && age >= 18 ? 'Ich will wählen, weil...' : 'Ich will wählen, weil...'}
                                        </span>
                                        {quote === 'custom' && (
                                            <>
                                                <Field
                                                    name="statement"
                                                    component="textarea"
                                                    placeholder="Dein eigenes Statement"
                                                    validate={(statement, values) => {
                                                        const age = 'age' in values && (values as FormState)?.age
                                                        return !statement || statement.length < 2
                                                    }}
                                                />
                                                <Error name="statement" />
                                            </>
                                        )}
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}
                    {page === 2 && (
                        <div className="form-container">
                            <div className="row">
                                <div className="col-25">
                                    <label>Wie heißt Du?</label>
                                </div>
                                <div className="col-75">
                                    <Field
                                        name="name"
                                        component="input"
                                        type="text"
                                        placeholder="Name"
                                        validate={(name) => (!name || name.length < 2) && 'Bitte gib Deinen Namen an!'}
                                    />
                                    <Error name="name" />
                                </div>
                            </div>
                        </div>
                    )}
                    {page === 3 && (
                        <div className="form-container">
                            <p>So sieht Deine fertige Kachel aus:</p>
                            <Preview displayAlways svgRef1={exportFile.svgRef1} svgRef2={exportFile.svgRef2} />
                            <div className="row">
                                <div className="col-25">
                                    <label>E-Mail (wird nicht veröffentlicht):</label>
                                </div>
                                <div className="col-75">
                                    <Field
                                        name="email"
                                        component="input"
                                        type="email"
                                        placeholder="deine.email@example.com"
                                        validate={(email) =>
                                            (!email ||
                                                !/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,})$/i.test(
                                                    email,
                                                )) &&
                                            'Unter welcher E-Mail-Adresse können wir Dich erreichen?'
                                        }
                                    />
                                    <Error name="email" />
                                </div>
                            </div>
                            {(isMobile && (
                                <p>
                                    Zunächst wird nur die erste Kachel heruntergeladen. Anschließend kannst Du die
                                    zweite Kachel herunterladen. Um diese selber auf Social Media zu veröffentlichen,
                                    speichere sie am besten zunächst auf Deinem Gerät ab und teile sie dann von dort
                                    aus.
                                </p>
                            )) || (
                                <p>
                                    Deine beiden Kacheln werden als zwei Dateien heruntergeladen. Um beide Dateien
                                    speichern zu können, wirst Du von Deinem Browser um Erlaubnis für den Download
                                    mehrerer Dateien gefragt. Die Dateien sind so benannt, dass sie leicht voneinander
                                    unterschieden werden können. Du kannst sie auf Deinen Social Media Accounts teilen.
                                </p>
                            )}
                            <p>
                                Durch Klick auf „Bestätigen“ nimmst Du an der Kampagne #meinezukunftmeinewahl des
                                Landesjugendrings NRW teil und bestätigst dein Einverständnis{' '}
                                <a
                                    href="https://www.ljr-nrw.de/wp-content/uploads/2022/02/Veroeffentlichungen-von-Personenbildern-und-Videoaufzeichnungen-nach-dem-Kunsturhebergesetz_Web.pdf"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    mit den Bedingungen.
                                </a>{' '}
                                Der Landesjugendring NRW kann deinen generierten Beitrag für seine Öffentlichkeitsarbeit
                                zur Kampagne nutzen. Dein Beitrag wird automatisch heruntergeladen und Du kannst ihn auf
                                Deinen eigenen Social Media Accounts teilen.
                            </p>
                        </div>
                    )}
                    {page === 4 && (
                        <>
                            {(isMobile && !downloadedSecond && (
                                <div className="form-container">
                                    <p>So sieht Deine fertige Kachel aus:</p>
                                    <Preview displayAlways svgRef1={exportFile.svgRef1} svgRef2={exportFile.svgRef2} />
                                    <button
                                        type="button"
                                        disabled={submitting || invalid}
                                        onClick={() => {
                                            exportFile.download(undefined, [undefined, exportFile.svgRef2])
                                            dispatch(downloadSecond())
                                        }}
                                    >
                                        Zweite Kachel downloaden
                                    </button>
                                </div>
                            )) || (
                                <>
                                    <p>
                                        Deine Kachel wurde erfolgreich erstellt.{' '}
                                        <strong>Teile Deine Kachel jetzt auf Social Media!</strong>
                                    </p>
                                    <p>
                                        Vielen Dank für Deine Unterstützung von{' '}
                                        <strong className="slogan">#meinezukunftmeinewahl</strong>!
                                    </p>
                                    <p>
                                        Du möchtest eine weitere Kachel gestalten?{' '}
                                        <button type="button" onClick={() => dispatch(reset())} disabled={submitting}>
                                            Hier entlang!
                                        </button>
                                    </p>
                                </>
                            )}
                        </>
                    )}

                    <div className="buttons">
                        {page > 0 && page < 4 && (
                            <button type="button" onClick={toPreviousPage} disabled={submitting}>
                                « Zurück
                            </button>
                        )}
                        {(page < 2 || (page === 2 && form.image)) && (
                            <button type="submit" disabled={submitting || invalid || (page === 0 && !form.image)}>
                                Weiter »
                            </button>
                        )}
                        {page === 3 && (
                            <>
                                <button
                                    type="submit"
                                    disabled={submitting || invalid}
                                    onClick={() =>
                                        isMobile
                                            ? exportFile.download(undefined, [exportFile.svgRef1])
                                            : exportFile.download()
                                    }
                                >
                                    {!isMobile && 'Bestätigen & Downloaden!'}
                                    {isMobile && 'Bestätigen & Beitrag teilen!'}
                                </button>
                                <br />
                                <button type="submit" disabled={submitting || invalid} className="link">
                                    Bestätigen {'&'} Beitrag ohne Download bereitstellen
                                </button>
                            </>
                        )}
                    </div>
                </form>
            )}
        </Form>
    )
}

export default InputForm
